import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Panel, toast } from '@/shared/ui';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { plannerQueryKeys } from '../../plannerApi';
import { serieFormAgenda } from './serieFormAgenda';
import { SerieFormFields } from './components/SerieFormFields';
import { useSerieForm } from './useSerieForm';

interface SerieFormPageProps {
  layerId: string;
}

/**
 * Formulář pro založení nové série pod produktem (Název série, Typ dopravy, Odpovědné osoby, Poznámka).
 */
export function SerieFormPage({ layerId }: SerieFormPageProps) {
  const { t } = useTranslation('planner');
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const { open } = useAgendaParams(serieFormAgenda, layerId);

  const closeForm = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const { form } = useSerieForm({
    productId: open.productId,
    onSaved: () => {
      void queryClient.invalidateQueries({ queryKey: plannerQueryKeys.tree() });
      toast({ severity: 'success', description: t('serie.toast.saved') });
      closeForm();
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('serie.toast.errorTitle'),
        description: t('serie.toast.saveError'),
      });
    },
  });

  return (
    <div className="bg-background h-full min-h-0 overflow-y-auto" data-testid="serie-form">
      <div className="mx-auto w-full max-w-220 p-4">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit();
          }}
        >
          <Panel
            variant="filled"
            title={t('serie.titleCreate')}
            footer={
              <div className="gap-phx-2 flex w-full items-center justify-end">
                <form.Subscribe
                  selector={(state) => ({
                    canSubmit: state.canSubmit,
                    isSubmitting: state.isSubmitting,
                  })}
                >
                  {({ canSubmit, isSubmitting }) => (
                    <>
                      <Button variant="ghost" size="md" type="button" onClick={closeForm}>
                        {t('serie.actions.cancel')}
                      </Button>
                      <Button
                        variant="primary"
                        size="md"
                        type="submit"
                        loading={isSubmitting}
                        disabled={!canSubmit || isSubmitting}
                        data-testid="serie-save-button"
                      >
                        {t('serie.actions.save')}
                      </Button>
                    </>
                  )}
                </form.Subscribe>
              </div>
            }
          >
            <SerieFormFields form={form} />
          </Panel>
        </form>
      </div>
    </div>
  );
}
